/**
 * Course Analytics Page - View detailed statistics for a course.
 */

import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconAlertTriangle,
  IconAcademicCap,
  IconUsers,
  IconCheck,
  IconRefresh,
  IconUser,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { useAdminCourse, useAdminCourseAnalytics } from "./hooks";
import type {
  CourseVisitorInfo,
  ExamInProgressInfo,
  ExamPassedInfo,
} from "./hooks";

// =============================================================================
// HELPERS
// =============================================================================

function formatName(
  firstName: string | null,
  lastName: string | null,
  username: string | null
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (username) return `@${username}`;
  return "Пользователь";
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// =============================================================================
// STAT CARD
// =============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  color: "blue" | "emerald" | "amber" | "slate";
}

function StatCard({ label, value, color }: StatCardProps) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-50 text-slate-600",
  };

  return (
    <div className={`p-4 rounded-xl ${colorClasses[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-75 mt-1">{label}</p>
    </div>
  );
}

// =============================================================================
// USER LIST COMPONENTS
// =============================================================================

interface VisitorRowProps {
  visitor: CourseVisitorInfo;
}

function VisitorRow({ visitor }: VisitorRowProps) {
  const { account } = visitor;
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
        <IconUser className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-900 truncate">
          {formatName(account.firstName, account.lastName, account.username)}
        </p>
        <p className="text-[11px] text-slate-400">
          {visitor.visitCount} визитов · {formatDate(visitor.lastVisit)}
        </p>
      </div>
    </div>
  );
}

interface InProgressRowProps {
  info: ExamInProgressInfo;
}

function InProgressRow({ info }: InProgressRowProps) {
  const { account } = info;
  const progress = Math.round((info.currentQuestion / info.totalQuestions) * 100);

  return (
    <div className="flex items-center gap-3 p-3">
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
        <IconRefresh className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-900 truncate">
          {formatName(account.firstName, account.lastName, account.username)}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-[10px] text-slate-400 flex-shrink-0">
            {info.currentQuestion}/{info.totalQuestions}
          </span>
        </div>
        {info.wrongAttempts > 0 && (
          <p className="text-[10px] text-red-400 mt-0.5">
            {info.wrongAttempts} ошибок
          </p>
        )}
      </div>
    </div>
  );
}

interface PassedRowProps {
  info: ExamPassedInfo;
}

function PassedRow({ info }: PassedRowProps) {
  const { account, attempt } = info;
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
        <IconCheck className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-slate-900 truncate">
          {formatName(account.firstName, account.lastName, account.username)}
        </p>
        <p className="text-[11px] text-slate-400">
          {attempt.score}/{attempt.maxScore} · {info.wrongAttempts} ошибок ·{" "}
          {formatDate(info.passedAt)}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export function CourseAnalyticsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  const { course, isLoading: isLoadingCourse, error: courseError } = useAdminCourse(
    courseId ?? ""
  );
  const { analytics, isLoading: isLoadingAnalytics, error: analyticsError, refetch } =
    useAdminCourseAnalytics(courseId ?? "");

  const isLoading = isLoadingCourse || isLoadingAnalytics;
  const error = courseError || analyticsError;

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      navigate(`/admin/academy/${courseId}`);
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, courseId]);

  if (isLoading) {
    return <PageLoader message="Загрузка аналитики..." />;
  }

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Ошибка</h2>
          <p className="text-sm text-slate-500 mb-4">
            {getErrorMessage(error, "Не удалось загрузить аналитику")}
          </p>
          <button onClick={refetch} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  if (!analytics) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <p className="text-slate-500">Нет данных</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
          <IconAcademicCap className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-900">Аналитика</h1>
          <p className="text-[13px] text-slate-500 truncate">{course?.title}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            refetch();
          }}
          className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 active:scale-95 transition-transform"
        >
          <IconRefresh className="w-5 h-5" />
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <StatCard
          label="Уникальных посетителей"
          value={analytics.visitors.length}
          color="blue"
        />
        <StatCard
          label="Всего визитов"
          value={analytics.totalVisitors}
          color="slate"
        />
        <StatCard
          label="Начали тест"
          value={analytics.examStartedCount}
          color="amber"
        />
        <StatCard
          label="Прошли тест"
          value={analytics.examPassedCount}
          color="emerald"
        />
      </div>

      {/* Additional stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-4 rounded-xl bg-white border border-slate-100">
          <p className="text-lg font-bold text-slate-900">
            {analytics.passRate.toFixed(0)}%
          </p>
          <p className="text-xs text-slate-500">Процент прохождения</p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-slate-100">
          <p className="text-lg font-bold text-slate-900">
            {analytics.averageWrongAttempts.toFixed(1)}
          </p>
          <p className="text-xs text-slate-500">Ср. ошибок на пользователя</p>
        </div>
      </div>

      {/* Passed users */}
      {analytics.passed.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <IconCheck className="w-4 h-4 text-emerald-500" />
            Прошли тест ({analytics.passed.length})
          </h2>
          <div className="card divide-y divide-slate-100">
            {analytics.passed.map((info) => (
              <PassedRow key={info.attempt.id} info={info} />
            ))}
          </div>
        </section>
      )}

      {/* In progress users */}
      {analytics.inProgress.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <IconRefresh className="w-4 h-4 text-amber-500" />
            В процессе ({analytics.inProgress.length})
          </h2>
          <div className="card divide-y divide-slate-100">
            {analytics.inProgress.map((info) => (
              <InProgressRow key={info.attempt.id} info={info} />
            ))}
          </div>
        </section>
      )}

      {/* Visitors */}
      {analytics.visitors.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <IconUsers className="w-4 h-4 text-blue-500" />
            Посетители ({analytics.visitors.length})
          </h2>
          <div className="card divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {analytics.visitors.map((visitor) => (
              <VisitorRow key={visitor.account.id} visitor={visitor} />
            ))}
          </div>
        </section>
      )}

      <div className="h-4" />
    </Layout>
  );
}
